import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { TableActionButton, TableActionIconButton } from "_shared/_views/tables/table-toolbar";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import { Link as RouterLink } from "react-router";

type InviteMembersButtonProps = {
  /** The narrow-layout form, for a title row with no room for a label. */
  iconOnly?: boolean;
};

/**
 * The Organization page's one way to invite people, which opens the colleague roster.
 *
 * There used to be two: "Invite member", a dialog for one named person, and this, for a list. The
 * roster takes one row as readily as thirty and asks for more than the dialog did (a title,
 * Marketplace publication, and the client whose roll-out the invitation names), so two buttons
 * offered a choice with no real difference and left people guessing which to press. The dialog's
 * other half, a link anyone can follow, now sits under the roster.
 *
 * It is also the roster's door that stays. The dashboard banner hides once put away or once the
 * organization is more than its leader, and the prompt raised on the Attorney Hub proposal's hire
 * is a notification that scrolls away.
 *
 * Gated on `canInviteColleagues`, the capability the roster page and its route are gated on, so the
 * link and the page behind it cannot disagree. It is narrower than administration access: someone
 * who administers only members or their permissions is not offered a grid the API would refuse. The
 * address names no firm: the bare route opens the reader's own.
 */
export default function InviteMembersButton(props: Readonly<InviteMembersButtonProps>) {
  const user = useSessionUser();
  if (!user?.canInviteColleagues()) return null;

  if (props.iconOnly === true) {
    return (
      <TableActionIconButton
        aria-label="Invite members"
        color="primary"
        component={RouterLink}
        to="/invite-colleagues"
      >
        <PersonAddIcon fontSize="large" />
      </TableActionIconButton>
    );
  }

  return (
    <TableActionButton component={RouterLink} startIcon={<PersonAddIcon />} to="/invite-colleagues">
      Invite members
    </TableActionButton>
  );
}
